import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, FileText, CheckCircle, TrendingUp, Users,
  Plus
} from 'lucide-react';
import PageHero from '../components/PageHero';
import apiClient from '../utils/apiClient';
import { calculateStats, getTaskTitle, getTaskFormat } from '../utils/statsUtils';

const AGENT_INITIALS = {
  'Content Strategist': 'CS',
  'Research Writer': 'RW',
  'Managing Editor': 'ME',
  'SEO Specialist & Copywriter': 'SE',
  'Fact-Checker / Verification Specialist': 'FC',
  'Philosopher / Thought Leader': 'TL',
  'Subject Matter Expert (SME)': 'SM',
};

const ALL_AGENTS = [
  'Content Strategist',
  'Research Writer',
  'Managing Editor',
  'SEO Specialist & Copywriter',
  'Fact-Checker / Verification Specialist',
  'Philosopher / Thought Leader',
  'Subject Matter Expert (SME)',
];

const STEP_NAMES = {
  1: 'Strategy', 2: 'Research', 3: 'Outline',
  4: 'Draft', 5: 'Fact-Check', 6: 'Editing',
  7: 'Approval', 8: 'Publishing', 9: 'Monitoring'
};

function getStepLabel(completedSteps) {
  const done = Object.keys(completedSteps || {}).length;
  const currentStep = done + 1;
  return `Step ${currentStep}: ${STEP_NAMES[currentStep] || 'Processing'}`;
}

function getProgress(completedSteps, totalSteps) {
  const done = Object.keys(completedSteps || {}).length;
  return totalSteps > 0 ? Math.round((done / totalSteps) * 100) : 0;
}

export default function ThePulse() {
  const navigate = useNavigate();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    apiClient.get('/workflow-instances/')
      .then(r => setInstances(r.data?.results || r.data || []))
      .catch(() => setInstances([]))
      .finally(() => setLoading(false));
  }, []);

  const stats = calculateStats(instances);
  const activeTasks = instances.filter(i => i.status === 'in_progress');
  const completedCount = stats.completed;
  const hasActiveWorkflows = activeTasks.length > 0;

  const layout = useMemo(() => ({
    topGrid: isMobile ? '1fr' : '1.2fr 1fr',
    bottomGrid: isMobile ? '1fr' : '1fr 1fr',
    activeGrid: isMobile ? '1fr' : '1fr 1fr',
    agentGrid: isMobile ? '1fr' : 'repeat(3, 1fr)',
    pagePadding: isMobile ? '20px 16px 96px' : '24px',
    titleSize: isMobile ? 18 : 22,
    statSize: isMobile ? 28 : 36,
    cardPadding: isMobile ? 16 : 20,
    quickCreateMinHeight: isMobile ? 0 : 200,
    sectionTitleSize: isMobile ? 18 : 22,
  }), [isMobile]);

  const page = {
    maxWidth: 900,
    margin: '0 auto',
    padding: layout.pagePadding,
    fontFamily: 'Inter, sans-serif',
    width: '100%',
    overflowX: 'hidden',
  };

  const card = {
    background: '#1A2236',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: layout.cardPadding,
    minWidth: 0,
    overflow: 'hidden',
  };

  const openStudio = (instanceId) => {
    navigate('/studio', { state: { workflowInstanceId: instanceId } });
  };

  return (
    <div style={page}>
      <PageHero
        badge="DASHBOARD"
        title="The Pulse"
        subtitle="Your creative command center. See everything happening across your content universe at a glance."
      />

      <div style={{ display: 'grid', gridTemplateColumns: layout.topGrid, gap: 16, marginBottom: 32 }}>
        <div style={{
          ...card,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gridRow: isMobile ? 'auto' : 'span 2',
          minHeight: layout.quickCreateMinHeight,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Zap size={18} color="#F97316" />
              <span style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500 }}>Quick Create</span>
            </div>
            <div style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: layout.titleSize,
              color: '#F1F5F9',
              marginBottom: 8,
              lineHeight: 1.2,
            }}>
              Start a new project
            </div>
            <p style={{ color: '#9CA3AF', fontSize: 14, lineHeight: 1.6, margin: 0 }}>
              Launch your AI writing team on a new task. Choose from 14 content formats and 12 writing styles.
            </p>
          </div>
          <button
            onClick={() => navigate('/task-creation')}
            style={{
              width: '100%',
              padding: '14px 0',
              background: '#F97316',
              color: 'white',
              border: 'none',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 24,
              fontFamily: 'Syne, sans-serif',
            }}
          >
            <Plus size={18} />
            Create Task
          </button>
        </div>

        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: layout.statSize, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', lineHeight: 1 }}>
              {loading ? '—' : stats.total}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 6 }}>Total Projects</div>
          </div>
          <FileText size={20} color="#6B7280" style={{ flexShrink: 0 }} />
        </div>

        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: layout.statSize, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', lineHeight: 1 }}>
              {loading ? '—' : completedCount}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 6 }}>Completed</div>
          </div>
          <CheckCircle size={20} color="#6B7280" style={{ flexShrink: 0 }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: layout.bottomGrid, gap: 16, marginBottom: 40 }}>
        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: layout.statSize, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', lineHeight: 1.05 }}>
              {loading ? '—' : stats.avgHours}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 6 }}>Avg. Completion Time</div>
          </div>
          <TrendingUp size={20} color="#6B7280" style={{ flexShrink: 0 }} />
        </div>
        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: layout.statSize, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', lineHeight: 1.05 }}>7</div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 6 }}>AI Agents Online</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
            <Users size={20} color="#6B7280" />
            <span style={{ fontSize: 13, fontWeight: 600, color: hasActiveWorkflows ? '#22C55E' : '#6B7280' }}>
              {hasActiveWorkflows ? `${Math.min(activeTasks.length * 2, 5)} active` : 'idle'}
            </span>
          </div>
        </div>
      </div>

      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: layout.sectionTitleSize, color: '#F1F5F9', marginBottom: 16 }}>
        Active Tasks
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: layout.activeGrid, gap: 16, marginBottom: 40 }}>
        {loading ? (
          <div style={{ color: '#6B7280', gridColumn: isMobile ? 'span 1' : 'span 2' }}>Loading...</div>
        ) : activeTasks.length === 0 ? (
          <div style={{ ...card, gridColumn: isMobile ? 'span 1' : 'span 2', color: '#6B7280', textAlign: 'center', padding: 32 }}>
            No active tasks.{' '}
            <button
              onClick={() => navigate('/task-creation')}
              style={{ color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              Create one →
            </button>
          </div>
        ) : activeTasks.slice(0, 4).map(inst => {
          const totalSteps = inst.total_steps || Object.keys(inst.completed_steps || {}).length + 1;
          const progress = getProgress(inst.completed_steps, totalSteps);
          const stepLabel = getStepLabel(inst.completed_steps);
          return (
            <div key={inst.id} style={{ ...card, cursor: 'pointer' }} onClick={() => openStudio(inst.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#F1F5F9', minWidth: 0, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                  {(() => { const t = getTaskTitle(inst); return t.length > 30 ? t.substring(0, 30) + '...' : t; })()}
                </div>
                <span style={{
                  background: 'rgba(249,115,22,0.12)',
                  color: '#F97316',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: 8,
                  whiteSpace: 'nowrap',
                  marginLeft: 8,
                  flexShrink: 0,
                }}>{stepLabel}</span>
              </div>
              <div style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 12 }}>
                {getTaskFormat(inst)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#6B7280' }}>
                <span>Progress</span>
                <span style={{ fontWeight: 600 }}>{progress}%</span>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 999 }}>
                <div style={{ height: '100%', width: `${progress}%`, background: '#1E3A8A', borderRadius: 999 }} />
              </div>
            </div>
          );
        })}
      </div>

      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: layout.sectionTitleSize, color: '#F1F5F9', marginBottom: 16 }}>
        Agent Status
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: layout.agentGrid, gap: isMobile ? 10 : 12 }}>
        {ALL_AGENTS.map((agent, i) => {
          const initials = AGENT_INITIALS[agent];
          const isActive = hasActiveWorkflows && i < 5;
          return (
            <div key={agent} style={{ ...card, display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 12, padding: isMobile ? '14px 16px' : 16, minWidth: 0, minHeight: isMobile ? 84 : undefined }}>
              <div style={{
                width: isMobile ? 42 : 40, height: isMobile ? 42 : 40, borderRadius: 12,
                background: '#1E3A8A',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 13, fontWeight: 700,
                flexShrink: 0,
              }}>{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: isMobile ? 14 : 13, lineHeight: 1.35, color: '#F1F5F9', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{agent}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: isActive ? '#22C55E' : '#4B5563',
                  }} />
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {isActive ? 'Active' : 'Idle'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
